import type { BridgeErrorCode } from "./types";

export interface WireError {
  code: BridgeErrorCode;
  message: string;
  /** What the device or driver said, when the bridge could say something plainer. */
  detail?: string;
}

/** Preview is live: a bridge and a client both drop frames rather than queue more than
 *  this, so a consumer that stalls resumes on current frames instead of a backlog. */
export const PREVIEW_QUEUE_MAX_FRAMES = 3;

export const ROUTES = {
  cameras: "/cameras",
  events: "/events",
  connect: (id: string) => `/cameras/${encodeURIComponent(id)}/connect`,
  disconnect: (id: string) => `/cameras/${encodeURIComponent(id)}/disconnect`,
  status: (id: string) => `/cameras/${encodeURIComponent(id)}/status`,
  properties: (id: string) => `/cameras/${encodeURIComponent(id)}/properties`,
  capture: (id: string) => `/cameras/${encodeURIComponent(id)}/capture`,
  preview: (id: string) => `/cameras/${encodeURIComponent(id)}/preview`,
} as const;

export const PREVIEW_CONTENT_TYPE = "application/x-kinoq-preview";

export const EVENTS_CONTENT_TYPE = "application/x-ndjson";

/** One counter per bridge, from 1, shared by every open stream — a subscriber joining
 *  mid-run sees the numbering continue. Nothing acts on it; it makes a dropped press
 *  visible in a log rather than invisible, which two observers who disagreed about the
 *  numbering could not do. */
export interface TriggerEvent {
  type: "trigger";
  seq: number;
}

export type BridgeEvent = TriggerEvent;

export function encodeTriggerLine(seq: number): Uint8Array {
  return new TextEncoder().encode(`${JSON.stringify({ type: "trigger", seq })}\n`);
}

/** Undefined for anything unrecognized, so an older tablet ignores a newer bridge's
 *  message rather than throwing. */
export function decodeBridgeEvent(text: string): BridgeEvent | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null) return undefined;
  const event = parsed as Record<string, unknown>;
  if (event.type !== "trigger") return undefined;
  if (typeof event.seq !== "number" || !Number.isInteger(event.seq)) return undefined;
  return { type: "trigger", seq: event.seq };
}

/** A keepalive a bridge may open the stream with or repeat between frames; the
 *  client reads it as a heartbeat and never yields it. */
export const PREVIEW_HELLO_PART_KIND = "hello";

export type PreviewPartKind = "hello" | "frame" | "error";

export interface PreviewPart {
  kind: PreviewPartKind;
  bytes: Uint8Array<ArrayBuffer>;
}

/** `<kind> <byteLength>\n`, ASCII. Transport framing only: frame metadata stays inside
 *  the JPEG bytes themselves. */
export function encodePreviewPartHeader(kind: PreviewPartKind, byteLength: number): Uint8Array {
  return new TextEncoder().encode(`${kind} ${byteLength}\n`);
}

const HEADER_RE = /^(hello|frame|error) (0|[1-9][0-9]*)\n$/;
// "error " plus the digits of any sane length fits well inside this.
const HEADER_MAX_BYTES = 32;

export class PreviewPartParser {
  private buffer: Uint8Array<ArrayBuffer> = new Uint8Array(0);

  /** Nonzero when the body ends means the stream was cut mid-part, not between parts. */
  get pendingBytes(): number {
    return this.buffer.length;
  }

  push(chunk: Uint8Array): PreviewPart[] {
    const joined = new Uint8Array(this.buffer.length + chunk.length);
    joined.set(this.buffer, 0);
    joined.set(chunk, this.buffer.length);
    this.buffer = joined;

    const parts: PreviewPart[] = [];
    while (true) {
      const nl = this.buffer.indexOf(0x0a);
      if (nl === -1) {
        if (this.buffer.length > HEADER_MAX_BYTES) {
          throw new Error("preview part header has no newline");
        }
        return parts;
      }
      const header = new TextDecoder().decode(this.buffer.slice(0, nl + 1));
      const m = HEADER_RE.exec(header);
      if (!m) throw new Error(`malformed preview part header: ${JSON.stringify(header)}`);
      const length = Number(m[2]);
      if (this.buffer.length < nl + 1 + length) return parts;
      parts.push({
        kind: m[1] as PreviewPartKind,
        bytes: this.buffer.slice(nl + 1, nl + 1 + length),
      });
      this.buffer = this.buffer.slice(nl + 1 + length);
    }
  }
}

/** Names a bridge SHOULD use where it has the capability; none are required and
 *  `listProperties()` is the only source of truth. `exposureCompensation` is in EV,
 *  `resolution` values are formatted "<width>x<height>". */
export const CONVENTIONAL_PROPERTIES = [
  "lock",
  "torch",
  "exposureCompensation",
  "resolution",
  "model",
] as const;
